<script setup lang="ts">
import { trustpilotLabel } from '~/composables/trustpilotLabel'
import { trustpilotReviewDate } from '~/composables/trustpilotReviewDate'

const props = withDefaults(defineProps<{
  businessUnitId: string
  locale?: string
  variant?: 'panel' | 'inline' | 'micro'
  heading?: string
  fallbackUrl?: string
}>(), {
  variant: 'panel',
  locale: 'en-US',
  fallbackUrl: 'https://www.trustpilot.com/review/180medical.com',
})

// Declared before the `await` below so the lifecycle hooks bind to the instance.
const track = ref<HTMLElement | null>(null)
const canPrev = ref(false)
const canNext = ref(false)

function updateArrows() {
  const el = track.value
  if (!el)
    return
  canPrev.value = el.scrollLeft > 4
  canNext.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
}

function scrollByPage(direction: number) {
  const el = track.value
  if (!el)
    return
  el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: 'smooth' })
}

const currentYear = new Date().getUTCFullYear()
const now = ref<number | null>(null)
let timeAgoTimer: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  updateArrows()
  globalThis.addEventListener('resize', updateArrows)
  now.value = Date.now()
  timeAgoTimer = setInterval(() => {
    now.value = Date.now()
  }, 60_000)
})
onBeforeUnmount(() => {
  globalThis.removeEventListener('resize', updateArrows)
  if (timeAgoTimer)
    clearInterval(timeAgoTimer)
})

function isoDate(iso: string) {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

const displayDate = (iso: string) => trustpilotReviewDate(iso, now.value, currentYear)
const absoluteDate = (iso: string) => trustpilotReviewDate(iso, null, currentYear)

const { data } = await useFetch('/api/trustpilot/business-unit', {
  query: { businessUnitId: props.businessUnitId, locale: props.locale },
  key: `tp-bu-${props.businessUnitId}-${props.locale}`,
})

const { data: reviewData, status: reviewStatus } = useLazyFetch('/api/trustpilot/reviews', {
  query: { businessUnitId: props.businessUnitId },
  key: `tp-reviews-${props.businessUnitId}`,
  server: false,
  immediate: props.variant === 'panel',
})

const bu = computed(() => data.value)
const reviews = computed(() => reviewData.value?.reviews ?? [])
const showReviews = computed(() =>
  props.variant === 'panel' && reviewStatus.value === 'success' && reviews.value.length > 0)
const ratingLabel = computed(() => trustpilotLabel(bu.value?.trustScore ?? 0))
</script>

<template>
  <a
    v-if="bu && variant === 'micro'"
    class="tp-micro"
    :href="bu.profileUrl || fallbackUrl"
    target="_blank"
    rel="noopener noreferrer"
    :aria-label="`${ratingLabel}: TrustScore ${bu.trustScore.toFixed(1)} out of 5 from ${bu.reviewCount.toLocaleString()} reviews on Trustpilot`"
    data-testid="company-trustpilot-micro"
  >
    <span
      v-if="ratingLabel"
      class="tp-micro__label"
    >{{ ratingLabel }}</span>
    <TrustpilotStars
      :rating="bu.stars"
      size="sm"
    />
    <span
      class="tp-micro__count"
    >{{ bu.reviewCount.toLocaleString() }} reviews</span>
    <TrustpilotLogo size="sm" />
  </a>

  <section
    v-else-if="bu"
    class="flex flex-col gap-4"
    data-testid="company-trustpilot-summary"
  >
    <h3
      v-if="heading"
      class="text-lg font-semibold text-med-deep-navy"
    >
      {{ heading }}
    </h3>

    <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
      <span
        v-if="ratingLabel"
        class="font-semibold text-med-deep-navy"
      >{{ ratingLabel }}</span>
      <TrustpilotStars
        :rating="bu.stars"
        :size="variant === 'panel' ? 'lg' : 'md'"
      />
      <p class="text-med-deep-navy">
        <strong>TrustScore {{ bu.trustScore.toFixed(1) }}</strong>
        <span class="text-med-dark-gray">
          · {{ bu.reviewCount.toLocaleString() }} reviews
        </span>
      </p>
    </div>

    <p
      v-if="showReviews"
      class="text-sm text-med-dark-gray"
      data-testid="company-trustpilot-review-disclosure"
    >
      Showing our latest reviews
    </p>

    <div
      v-if="showReviews"
      class="relative"
    >
      <section
        ref="track"
        class="tp-carousel flex gap-3 overflow-x-auto scroll-smooth pb-1 snap-x snap-mandatory"
        tabindex="0"
        aria-label="Customer reviews"
        @scroll="updateArrows"
      >
        <article
          v-for="(r, i) in reviews"
          :key="i"
          class="flex w-60 shrink-0 snap-start flex-col gap-1.5 rounded-xl border border-med-light-gray p-3"
        >
          <div class="flex items-center gap-2">
            <TrustpilotStars
              :rating="r.ratingValue"
              size="sm"
            />
            <span
              v-if="r.invited"
              class="text-xs text-med-dark-gray"
            >Invited</span>
          </div>
          <p
            v-if="r.title"
            class="line-clamp-2 text-sm font-semibold text-med-deep-navy"
          >
            {{ r.title }}
          </p>
          <p
            v-if="r.body"
            class="line-clamp-3 text-sm text-med-dark-gray"
          >
            {{ r.body }}
          </p>
          <p class="mt-auto pt-1 text-xs text-med-dark-gray">
            {{ r.author }} ·
            <time
              :datetime="isoDate(r.datePublished)"
              :title="absoluteDate(r.datePublished)"
            >{{ displayDate(r.datePublished) }}</time>
          </p>
        </article>
      </section>

      <button
        v-show="canPrev"
        type="button"
        class="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-primary p-1.5 text-white shadow-md hover:bg-secondary cursor-pointer"
        aria-label="Previous reviews"
        @click="scrollByPage(-1)"
      >
        <svg
          class="size-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <button
        v-show="canNext"
        type="button"
        class="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-primary p-1.5 text-white shadow-md hover:bg-secondary cursor-pointer"
        aria-label="More reviews"
        @click="scrollByPage(1)"
      >
        <svg
          class="size-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
    </div>

    <a
      v-if="bu.profileUrl"
      class="inline-flex w-fit items-center gap-2 text-med-deep-navy underline underline-offset-2 hover:text-med-dark-gray"
      :href="bu.profileUrl"
      target="_blank"
      rel="noopener noreferrer"
    >
      See our reviews on
      <TrustpilotLogo size="sm" />
    </a>
  </section>

  <a
    v-else
    class="inline-flex w-fit items-center gap-2 text-med-deep-navy underline underline-offset-2 hover:text-med-dark-gray"
    :href="fallbackUrl"
    target="_blank"
    rel="noopener noreferrer"
    data-testid="company-trustpilot-fallback"
  >
    See our reviews on
    <TrustpilotLogo size="sm" />
  </a>
</template>

<style scoped>
.tp-micro {
  display: inline-flex;
  width: fit-content;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-med-deep-navy, #0a2d38);
  text-decoration: none;
}
.tp-micro__label {
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.tp-micro__count {
  font-size: 0.875rem;
}
.tp-micro:hover .tp-micro__label,
.tp-micro:focus-visible .tp-micro__label {
  text-decoration: none;
}
.tp-micro:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 3px;
}

.dark-bg .tp-micro {
  color: #fff;
}

article :deep(.tp-stars__tile rect) {
  transition: filter 0.15s ease;
}
article:hover :deep(.tp-stars__tile rect) {
  filter: brightness(0.85);
}

.tp-carousel {
  scrollbar-width: none;
}
.tp-carousel::-webkit-scrollbar {
  display: none;
}

.tp-carousel:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
</style>
